import { defineStore } from 'pinia'
import { Centrifuge, UnauthorizedError, type Subscription } from 'centrifuge'
import { markRaw } from 'vue'
import axios from 'axios'
import { BASE_URL } from '@/environment/environment'
import { currentAuthEpoch } from '@/utils/authRecovery'
import { createRealtimeRefresh } from '@/modules/centrifuge/services/realtimeRefresh'
import { useAuthStore } from '@/modules/authStore/stores/authStore'

// локальный хелпер: превращает что угодно в JSON-safe POJO
const toSafe = (v: unknown, seen = new WeakSet<object>()): any => {
  if (
    v == null
    || typeof v === 'string'
    || typeof v === 'number'
    || typeof v === 'boolean'
  ) {
    return v
  }
  if (v instanceof Date) return v.toISOString()
  if (v instanceof Error) {
    return {
      name: v.name,
      message: v.message,
      stack: (v.stack || '').split('\n').slice(0, 5),
    }
  }

  if (Array.isArray(v)) return v.map(x => toSafe(x, seen))

  if (typeof v === 'object') {
    const obj = v as Record<string, unknown>
    if (seen.has(obj)) return '[circular]'
    seen.add(obj)

    // Коллекции → безопасный вид
    if (v instanceof Map) {
      const out: any[] = []
      for (const [k, val] of v.entries()) {
        out.push({ key: String(k), value: toSafe(val as unknown, seen) })
      }

      return out
    }
    if (v instanceof Set) {
      const out: any[] = []
      for (const val of v.values()) out.push(toSafe(val as unknown, seen))

      return out
    }
    if (ArrayBuffer.isView(v)) {
      return `[typed array length=${(v as ArrayBufferView).byteLength}]`
    }
    if (v instanceof ArrayBuffer) return `[arraybuffer length=${v.byteLength}]`

    // Обычный объект
    const out: Record<string, any> = {}
    for (const key of Object.keys(obj)) {
      const val = obj[key]
      if (typeof val === 'function' || typeof val === 'symbol') continue
      out[key] = toSafe(val, seen)
    }

    return out
  }

  // Фолбэк
  try {
    return JSON.parse(JSON.stringify(v))
  } catch {
    return String(v)
  }
}

type PublicationHandler = (data: any) => void | Promise<void>

export const useCentrifugeStore = defineStore('centrifuge', {
  state: () => ({
    client: null as Centrifuge | null,
    connected: false,
    connecting: false,
    connectionLost: false,
    establishedChannels: markRaw(new Set<string>()),
    subscriptions: markRaw(new Map<string, Subscription>()),
    handlers: markRaw(new Map<string, PublicationHandler>()),
    recoveryHandlers: markRaw(new Map<string, ReturnType<typeof createRealtimeRefresh>>()),
    logs: [] as Array<{ time: string; type: string; details: unknown }>,
    // Changes on first subscribe or when history cannot bridge a gap. Mounted
    // views reload their authoritative API state; a socket alone is not recovery.
    recoveryRevision: 0,
  }),

  actions: {
    logEvent(type: string, details: unknown) {
      const safe = toSafe(details)
      this.logs.push({ time: new Date().toISOString(), type, details: safe })
      if (this.logs.length > 200) this.logs.shift()
      console.debug(`[CENTRIFUGE] ${type} ${JSON.stringify(safe)}`)
    },

    async initCentrifuge() {
      const authStore = useAuthStore()
      if (!authStore.isAuthenticated || this.client) return

      // The same-origin route is also Django's default. This lets the SDK retry
      // getToken even if the initial refresh endpoint was temporarily unavailable.
      const endpoint = new URL('/connection/websocket', BASE_URL || window.location.origin)
      endpoint.protocol = endpoint.protocol === 'https:' ? 'wss:' : 'ws:'
      const epoch = currentAuthEpoch()
      const tokenIsFresh = authStore.centrifugeTokenExpiry
        && new Date(authStore.centrifugeTokenExpiry).getTime() > Date.now() + 30_000
      const client: Centrifuge = markRaw(new Centrifuge(authStore.centrifugeUrl || endpoint.toString(), {
        token: tokenIsFresh ? authStore.centrifugeToken || '' : '',
        getToken: async () => {
          if (this.client !== client || epoch !== currentAuthEpoch() || !authStore.isAuthenticated)
            throw new UnauthorizedError('Realtime session ended')
          try {
            await authStore.refreshCentrifugeToken({ throwOnError: true })
          } catch (error) {
            if (!authStore.isAuthenticated || (axios.isAxiosError(error) && [401, 403].includes(error.response?.status || 0)))
              throw new UnauthorizedError('Realtime session ended')
            throw error // The SDK retries temporary failures with backoff.
          }
          if (this.client !== client || epoch !== currentAuthEpoch() || !authStore.isAuthenticated)
            throw new UnauthorizedError('Realtime session ended')
          if (!authStore.centrifugeToken) throw new Error('Missing realtime token')
          return authStore.centrifugeToken
        },
      }))
      this.client = client
      this.connecting = true
      let wasConnected = false

      client.on('connecting', ctx => {
        if (this.client !== client) return
        this.connected = false
        this.connecting = true
        this.connectionLost = wasConnected
        console.warn('🟡 [CF WebSocket] Подключение к серверу (connecting)...', ctx)
        this.logEvent('Connecting', ctx)
      })
      client.on('connected', ctx => {
        if (this.client !== client) return
        wasConnected = true
        this.connected = true
        this.connecting = false
        this.connectionLost = false
        console.info('🟢 [CF WebSocket] Успешно подключен (connected)', ctx)
        this.logEvent('Connected successfully', ctx)
      })
      client.on('disconnected', ctx => {
        if (this.client !== client) return
        this.connected = false
        this.connecting = false
        this.connectionLost = wasConnected
        console.warn('🔴 [CF WebSocket] Отключен от сервера (disconnected)!', ctx)
        this.logEvent('Disconnected', ctx)
        // Terminal disconnects (logout, revoked access) must stay disconnected.
        // Temporary transport failures are handled by the SDK's connecting state.
      })
      client.on('error', ctx => {
        if (this.client === client) {
          console.error('🚨 [CF WebSocket] Ошибка транспорта:', ctx)
          this.logEvent('Error occurred', ctx)
        }
      })
      for (const channel of this.handlers.keys()) this.centrifugeSubscribe(channel)
      client.connect()
    },

    centrifugeSubscribe(channel: string) {
      const client = this.client
      if (!client || this.subscriptions.has(channel)) return
      const sub = markRaw(client.newSubscription(channel, { positioned: true, recoverable: true }))
      this.subscriptions.set(channel, sub)
      const isCurrent = () => this.client === client && this.subscriptions.get(channel) === sub
      sub.on('publication', ctx => {
        if (!isCurrent()) return
        this.logEvent('Received publication', { channel, event_type: ctx.data?.event_type, offset: ctx.offset })
        // Dispatch promises must be observed too; synchronous try/catch misses them.
        void Promise.resolve().then(() => {
          if (isCurrent()) return this.handlers.get(channel)?.(ctx.data)
        }).catch(error => {
          console.error(`🚨 [CF Realtime] Ошибка обработчика события в канале ${channel}:`, error)
          this.logEvent('Handler error', error)
        })
      })
      sub.on('subscribed', ctx => {
        if (!isCurrent()) return
        const isReconnect = this.establishedChannels.has(channel)
        this.logEvent('Subscription successful', { channel, recovered: ctx.recovered, wasRecovering: ctx.wasRecovering, isReconnect })
        if (ctx.recovered) {
          console.info(`📡 [CF Realtime] Подписка на канал "${channel}" восстановлена БЕЗ потерь (recovered=true, догрузка не требуется)`)
        } else if (isReconnect || ctx.wasRecovering || this.connectionLost) {
          console.warn(`⚠️ [CF Realtime] ОБРЫВ СВЯЗИ в канале "${channel}"! (recovered=false после разрыва). Возможна потеря сообщений. Запуск recoveryRevision++ и инвалидации подписчиков...`)
          this.recoveryRevision++
          this.recoveryHandlers.get(channel)?.invalidate()
        } else {
          console.info(`📡 [CF Realtime] Первичная подписка на канал "${channel}" оформлена (первый вход, догрузка не требуется)`)
        }
        this.establishedChannels.add(channel)
      })
      sub.on('error', ctx => {
        if (isCurrent()) {
          console.error(`🚨 [CF Realtime] Ошибка подписки на канал "${channel}":`, ctx)
          this.logEvent('Subscription error', { channel, error: ctx })
        }
      })
      sub.on('unsubscribed', ctx => {
        if (isCurrent()) {
          console.warn(`🔌 [CF Realtime] Отписка от канала "${channel}"`, ctx)
          this.logEvent('Unsubscribed', { channel, code: ctx.code, reason: ctx.reason })
        }
      })
      sub.subscribe()
    },

    async subscribe(channel: string, onPublication: PublicationHandler, onRecovery?: () => Promise<void>) {
      this.handlers.set(channel, onPublication)
      if (onRecovery) {
        this.recoveryHandlers.get(channel)?.stop()
        this.recoveryHandlers.set(channel, createRealtimeRefresh(onRecovery, error => this.logEvent('Recovery handler error', error)))
      }
      await this.initCentrifuge()
      this.centrifugeSubscribe(channel)
    },

    centrifugeUnsubscribe(channel: string) {
      this.handlers.delete(channel)
      this.recoveryHandlers.get(channel)?.stop()
      this.recoveryHandlers.delete(channel)
      this.establishedChannels.delete(channel)
      const sub = this.subscriptions.get(channel)
      this.subscriptions.delete(channel)
      if (sub) this.client?.removeSubscription(sub)
    },

    disconnect() {
      const client = this.client
      this.client = null // Ignore late callbacks from the previous session.
      this.handlers.clear()
      for (const recovery of this.recoveryHandlers.values()) recovery.stop()
      this.recoveryHandlers.clear()
      for (const sub of this.subscriptions.values()) client?.removeSubscription(sub)
      this.subscriptions.clear()
      this.establishedChannels.clear()
      client?.disconnect()
      this.connected = false
      this.connecting = false
      this.connectionLost = false
    },

    diagnostics() {
      return {
        connected: this.connected,
        connecting: this.connecting,
        connectionLost: this.connectionLost,
        hasClient: !!this.client,
        subscriptions: Array.from(this.subscriptions.keys()),
        recoveryRevision: this.recoveryRevision,
        lastLog: this.logs[this.logs.length - 1] || null,
      }
    },
  },

  getters: {
    connectionStatus: state => state.connecting ? 'connecting' : state.connected ? 'connected' : 'disconnected',
    isOnline: state => state.connected,
  },
})
